/**
 * Hook for fetching entity module tab counts (All Entities).
 * Provides counts for the entities module landing page tabs.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/api-client';
import { useAuth } from '@/store/auth.store';
import { useOrganizationStore } from '@/store/organization.store';

export interface EntityModuleCounts {
  allEntities: number;
}

interface UseEntityModuleCountsOptions {
  enabled?: boolean;
}

// Query key for entity module counts
const entityModuleCountsKey = ['entity-module-counts'] as const;

/**
 * Fetches entity module counts
 */
async function fetchEntityModuleCounts(): Promise<EntityModuleCounts> {
  return apiClient<EntityModuleCounts>('/entities/module-counts');
}

/**
 * Hook for fetching entity module counts.
 * Returns count for "All Entities".
 */
export function useEntityModuleCounts({ enabled = true }: UseEntityModuleCountsOptions = {}) {
  const queryClient = useQueryClient();
  const user = useAuth((state) => state.user);
  const currentOrganization = useOrganizationStore((state) => state.currentOrganization);

  const { data, isLoading, error } = useQuery({
    queryKey: [...entityModuleCountsKey, currentOrganization?.id, user?.id],
    queryFn: fetchEntityModuleCounts,
    enabled: enabled && !!currentOrganization?.id && !!user?.id,
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  const counts = {
    allEntities: data?.allEntities,
  };

  /**
   * Invalidate and refetch counts
   */
  const refetchCounts = () => {
    queryClient.invalidateQueries({ queryKey: entityModuleCountsKey });
  };

  return {
    counts,
    isLoading,
    error,
    refetchCounts,
  };
}

// Export query key for external use
export { entityModuleCountsKey };
